// LAP-550 — Coque commune pour toutes les cards de l'app Investissements
import React from 'react'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import Paper from '@mui/material/Paper'
import { Tooltip, Box } from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import { invTheme } from './theme'

const Shell = styled(Paper, {
  shouldForwardProp: (prop) =>
    prop !== 'accent' && prop !== 'minHeight' && prop !== 'clickable',
})(({ accent, minHeight = 450, clickable }) => ({
  position: 'relative',
  padding: 18,
  paddingLeft: 22,
  background: invTheme.cardBg,
  color: 'white',
  height: minHeight,
  overflow: 'hidden',
  border: invTheme.cardBorder,
  borderRadius: invTheme.cardRadius,
  boxShadow: invTheme.cardShadow,
  transition: 'border-color 200ms ease, transform 200ms ease, box-shadow 200ms ease',
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    borderColor: clickable ? 'rgba(99,160,255,0.45)' : 'rgba(99,160,255,0.25)',
    boxShadow: clickable
      ? '0 6px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)'
      : invTheme.cardShadow,
    '& .card-expand-hint': { opacity: 1 },
  },
  '&::before': accent
    ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 14,
        bottom: 14,
        width: 3,
        borderRadius: 2,
        background: accent,
        opacity: 0.85,
      }
    : undefined,
}))

// LAP-550 V1.2 — `detailTo` rend la card cliquable et navigue vers la page détail.
// Les clics sur boutons/inputs internes ne déclenchent pas la nav grâce à
// `data-noclick="true"` (ajouté par les composants enfants qui veulent intercepter).
export default function CardShell({ accent, children, minHeight, detailTo }) {
  const navigate = useNavigate()

  const handleClick = (e) => {
    if (!detailTo) return
    // Ignore les clics dans les composants interactifs (boutons, inputs, tables, etc.)
    //
    // LAP — un <Select>/<Autocomplete>/<DatePicker> MUI rend son menu/popup
    // dans un Portal attaché à document.body : physiquement, une MenuItem
    // cliquée n'est PAS un descendant DOM de cette card. Mais React fait
    // bubbler l'événement synthétique à travers l'ARBRE REACT (pas le DOM
    // physique), donc ce `onClick` se déclenche quand même. Comme la
    // MenuItem cliquée (role="option", balise <li>) ne matchait aucun des
    // sélecteurs ci-dessous, `interactive` restait faux et la card
    // naviguait vers `detailTo` en pleine sélection d'une option — ça
    // fermait aussi tout Dialog ouvert au-dessus (ex: popup de profil de
    // recherche Immobilier). On exclut donc aussi les portails/menus/popups
    // MUI eux-mêmes.
    const interactive = e.target.closest(
      'button, a, input, textarea, select, [role="button"], [role="option"], [role="listbox"], [data-noclick="true"], .MuiDataGrid-root, .MuiIconButton-root, .MuiCheckbox-root, .MuiPopover-root, .MuiMenu-root, .MuiDialog-root, .MuiModal-root, .MuiAutocomplete-popper, .MuiPickersPopper-root',
    )
    if (interactive) return
    navigate(detailTo)
  }

  return (
    <Shell accent={accent} minHeight={minHeight} clickable={!!detailTo} onClick={handleClick}>
      {detailTo && (
        <Tooltip title="Voir le détail" placement="left">
          <Box
            className="card-expand-hint"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              opacity: 0,
              transition: 'opacity 150ms ease',
              color: 'rgba(255,255,255,0.55)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <OpenInFullIcon sx={{ fontSize: 14 }} />
          </Box>
        </Tooltip>
      )}
      {children}
    </Shell>
  )
}
